import { UserAIStats, UserDetails, AICompletionType } from "@knowt/syncing/graphql/schema";
import { getUserPlan } from "../user/graphqlUtils";
import { AI_USAGE_LIMITS } from "../user/types";

export const DEFAULT_AI_STATS: UserAIStats = {
    curPrompts: 0,
    curTokens: 0,
    videos: 0,
    vNotes: 0,
    vSets: 0,
    mins: 0,
    pdfs: 0,
    pages: 0,
    nTests: 0,
    nSets: 0,
    chats: 0,
    explain: 0,
    frq: 0,
    mNotes: 0,
    mSets: 0,
    tools: 0,
    assignments: 0,
    scans: 0,
    aiV: 0,
    mock: 0,
    history: [],
};

export const verifyAIUsage = (
    user: Pick<UserDetails, "orgPlanType" | "subscriptionType" | "ai"> | null | undefined,
    type: AICompletionType
) => {
    if (!user) return false;

    // Use subscription type
    const subscriptionType = getUserPlan(user);

    const AI_USAGE_LIMIT = AI_USAGE_LIMITS[subscriptionType];

    const aiUsage = user.ai || DEFAULT_AI_STATS;
    const curPrompts = aiUsage.curPrompts ?? 0;
    const nTests = aiUsage.nTests ?? 0;
    const nSets = aiUsage.nSets ?? 0;
    const chats = aiUsage.chats ?? 0;
    const explain = aiUsage.explain ?? 0;
    const mSets = aiUsage.mSets ?? 0;
    const mNotes = aiUsage.mNotes ?? 0;
    const tools = aiUsage.tools ?? 0;
    const frq = aiUsage.frq ?? 0;
    const assignments = aiUsage.assignments ?? 0;
    const scans = aiUsage.scans ?? 0;
    const aiV = aiUsage.aiV ?? 0;

    const throwLimitError = (type: AICompletionType) => {
        throw new Error(`Over ${type} usage limit`);
    };

    // update the correct values
    switch (type) {
        case AICompletionType.NOTE_CHAT: {
            if (chats >= (AI_USAGE_LIMIT.chats ?? 0)) {
                throwLimitError(type);
            }
            break;
        }
        case AICompletionType.FLASHCARD_CHAT: {
            if (chats >= (AI_USAGE_LIMIT.chats ?? 0)) {
                throwLimitError(type);
            }
            break;
        }
        case AICompletionType.NOTE_FLASHCARDS: {
            if (nSets >= (AI_USAGE_LIMIT.nSets ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.NOTE_TEST:
        case AICompletionType.GENERATE_SIMILAR_QUESTIONS: {
            if (nTests >= (AI_USAGE_LIMIT.nTests ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.MEDIA_FLASHCARDS: {
            if (mSets >= (AI_USAGE_LIMIT.mSets ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.MEDIA_NOTES: {
            if (mNotes >= (AI_USAGE_LIMIT.mNotes ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.EXPLAIN_WHY_IM_WRONG: {
            if (explain >= (AI_USAGE_LIMIT.explain ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.EXPLAIN_EXAM_FRQ_ANSWER: {
            if (frq >= (AI_USAGE_LIMIT.frq ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.SCAN_AND_SOLVE: {
            if (scans >= (AI_USAGE_LIMIT.scans ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.VIDEO_GENERATION: {
            if (aiV >= (AI_USAGE_LIMIT.aiV ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        case AICompletionType.LESSON_PLAN:
        case AICompletionType.RUBRIC_GENERATOR:
        case AICompletionType.WRITING_FEEDBACK:
        case AICompletionType.READING_TEXT:
        case AICompletionType.WORKSHEET_GENERATOR:
        case AICompletionType.TEXT_LEVELER:
        case AICompletionType.PROFESSIONAL_EMAIL:
        case AICompletionType.REPORT_CARD_COMMENTS:
        case AICompletionType.TEXT_PROOFREADER:
        case AICompletionType.TEACHER_TOOL_FIELDS_HELPER:
        case AICompletionType.TEXT_PARAPHRASER:
        case AICompletionType.MAKE_IT_RELEVANT:
        case AICompletionType.CLASS_NEWSLETTER_GENERATOR:
        case AICompletionType.VOCABULARY_LIST_GENERATOR:
        case AICompletionType.DISCUSSION_PROMPT_GENERATOR:
        case AICompletionType.WRITING_PROMPT_GENERATOR:
        case AICompletionType.MATH_WORD_PROBLEM_GENERATOR:
        case AICompletionType.SCIENCE_LAB_GENERATOR:
        case AICompletionType.MULTIPLE_CHOICE_QUIZ_GENERATOR:
        case AICompletionType.DEBATE_SCENARIO_GENERATOR:
        case AICompletionType.ICE_BREAKER_ACTIVITIES:
        case AICompletionType.CONTENT_SUMMARIZER:
        case AICompletionType.JEOPARDY_GAME_GENERATOR:
        case AICompletionType.DECODABLE_TEXT_GENERATOR:
        case AICompletionType.STANDARDS_UNPACKER:
        case AICompletionType.MATH_SPIRAL_REVIEW_GENERATOR:
        case AICompletionType.JOKE_GENERATOR:
        case AICompletionType.TEXT_TRANSLATOR:
        case AICompletionType.STORY_WORD_PROBLEMS_GENERATOR:
        case AICompletionType.BIP_GENERATOR:
        case AICompletionType.TIME_BASED_ACTIVITY_GENERATOR:
        case AICompletionType.RECOMMENDATION_LETTER_GENERATOR:
        case AICompletionType.PERFORMANCE_TASK_GENERATOR:
        case AICompletionType.PLC_AGENDA_GENERATOR:
        case AICompletionType.SYLLABUS_GENERATOR:
        case AICompletionType.SONG_GENERATOR: {
            if (tools >= (AI_USAGE_LIMIT.tools ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        // IGNORED
        case AICompletionType.ASSIGNMENT_RUBRIC_GENERATOR:
        case AICompletionType.ASSIGNMENT_PRIOR_KNOWLEDGE_GENERATOR:
        case AICompletionType.ASSIGNMENT_GRADER:
        case AICompletionType.ASSIGNMENT_FEEDBACK_GENERATOR:
        case AICompletionType.ASSIGNMENT_MISC_INITIAL_SETTINGS:
        case AICompletionType.ASSIGNMENT_RULES_GENERATOR:
        case AICompletionType.ASSIGNMENT_STANDARDS_GENERATOR:
        case AICompletionType.ASSIGNMENT_CHAT:
            break;

        case AICompletionType.ASSIGNMENT_TITLE_DESC_GENERATOR: {
            if (assignments >= (AI_USAGE_LIMIT.assignments ?? 0)) {
                throwLimitError(type);
            }
            break;
        }

        // biome-ignore lint: complexity/noUselessSwitchCase
        case AICompletionType.FLASHCARD_AUTOCOMPLETE:
        // biome-ignore lint: complexity/noUselessSwitchCase
        case AICompletionType.NOTE_AUTOCOMPLETE:
        default: {
            if (curPrompts >= (AI_USAGE_LIMIT.prompts ?? 0)) {
                throwLimitError(type);
            }
            break;
        }
    }

    return true;
};
